/*
 * Semantic colour maps.
 *
 * Ported from html-markup's PRODUCTIVITY / ROLE_TAG / VERSION_TAG / AVATAR_TONES.
 * These are here rather than inline in components so a status means the same
 * colour on every page — green is productive everywhere, amber is "needs
 * attention" everywhere.
 *
 * `gradient` is the pair the SVG bar charts need; every other entry is a class
 * applied directly. Both stay here so a bar and a tag for the same status can
 * never drift apart.
 *
 * Every class is a COMPLETE string literal on purpose. Tailwind scans source
 * text, so `bg-${tone}-50` would never be generated and the element would render
 * unstyled. Never assemble a colour class.
 */

/* ---- Generic tone, used by Tag, StatCard, ProgressBar ------------------- */
export const TONES = {
    brand: {
        tag: "bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-200",
        icon: "bg-brand-50 text-brand-600 dark:bg-brand-950 dark:text-brand-300",
        text: "text-brand-600 dark:text-brand-400",
        bar: "bg-brand-500",
        gradient: "from-brand-500 to-brand-400",
        edge: "bg-brand-500",
        dot: "bg-brand-500",
    },
    emerald: {
        tag: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400",
        icon: "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400",
        text: "text-emerald-600 dark:text-emerald-400",
        bar: "bg-emerald-500",
        gradient: "from-emerald-500 to-emerald-400",
        edge: "bg-emerald-500",
        dot: "bg-emerald-500",
    },
    amber: {
        tag: "bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-400",
        icon: "bg-amber-50 text-amber-600 dark:bg-amber-950/60 dark:text-amber-400",
        text: "text-amber-600 dark:text-amber-400",
        bar: "bg-amber-500",
        gradient: "from-amber-500 to-amber-400",
        edge: "bg-amber-500",
        dot: "bg-amber-500",
    },
    rose: {
        tag: "bg-rose-50 text-rose-700 dark:bg-rose-950/60 dark:text-rose-400",
        icon: "bg-rose-50 text-rose-600 dark:bg-rose-950/60 dark:text-rose-400",
        text: "text-rose-600 dark:text-rose-400",
        bar: "bg-rose-500",
        gradient: "from-rose-500 to-rose-400",
        edge: "bg-rose-500",
        dot: "bg-rose-500",
    },
    violet: {
        tag: "bg-violet-50 text-violet-700 dark:bg-violet-950/60 dark:text-violet-300",
        icon: "bg-violet-50 text-violet-600 dark:bg-violet-950/60 dark:text-violet-300",
        text: "text-violet-600 dark:text-violet-300",
        bar: "bg-violet-500",
        gradient: "from-violet-500 to-violet-400",
        edge: "bg-violet-500",
        dot: "bg-violet-500",
    },
    cyan: {
        tag: "bg-cyan-50 text-cyan-700 dark:bg-cyan-950/60 dark:text-cyan-300",
        icon: "bg-cyan-50 text-cyan-600 dark:bg-cyan-950/60 dark:text-cyan-300",
        text: "text-cyan-600 dark:text-cyan-300",
        bar: "bg-cyan-500",
        gradient: "from-cyan-500 to-cyan-400",
        edge: "bg-cyan-500",
        dot: "bg-cyan-500",
    },
    orange: {
        tag: "bg-orange-50 text-orange-700 dark:bg-orange-950/60 dark:text-orange-300",
        icon: "bg-orange-50 text-orange-600 dark:bg-orange-950/60 dark:text-orange-300",
        text: "text-orange-600 dark:text-orange-300",
        bar: "bg-orange-500",
        gradient: "from-orange-500 to-orange-400",
        edge: "bg-orange-500",
        dot: "bg-orange-500",
    },
    slate: {
        tag: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
        icon: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
        text: "",
        bar: "bg-slate-400",
        gradient: "from-slate-400 to-slate-300",
        edge: "bg-slate-400",
        dot: "bg-slate-400",
    },
};

export const toneOf = (name) => TONES[name] ?? TONES.slate;

/* ---- Productivity status ------------------------------------------------ */
export const PRODUCTIVITY = {
    productive: { label: "Productive", tone: "emerald" },
    neutral: { label: "Neutral", tone: "slate" },
    unproductive: { label: "Unproductive", tone: "rose" },
    unrated: { label: "Unrated", tone: "brand" },
};

/* ---- Roles -------------------------------------------------------------- */
export const ROLES = {
    superAdmin: { label: "Super Admin", tone: "violet" },
    admin: { label: "Admin", tone: "rose" },
    manager: { label: "Manager", tone: "brand" },
    employee: { label: "Employee", tone: "emerald" },
};

/* ---- Desktop app version tiers -----------------------------------------
 * Four buckets because "old" means different things operationally: a pre-2.0
 * build predates the data-stall fixes and needs chasing down, whereas one patch
 * behind is routine. See appDoc/ for the field reports behind this.
 * ---------------------------------------------------------------------- */
export const VERSION_TIERS = {
    current: { label: "Latest", tone: "emerald" },
    behind: { label: "Update", tone: "amber" },
    stale: { label: "Outdated", tone: "rose" },
    none: { label: "Not installed", tone: "slate" },
    // No published build to compare against — see versionTier.
    unknown: { label: "Unknown", tone: "slate" },
};

/* ---- Avatar colours -----------------------------------------------------
 * Deterministic per name so the same person is always the same colour, which
 * makes scanning a long table much easier.
 * ---------------------------------------------------------------------- */
const AVATAR_TONES = [
    "bg-brand-600",
    "bg-emerald-600",
    "bg-violet-600",
    "bg-orange-500",
    "bg-cyan-600",
    "bg-pink-600",
];

export function avatarTone(name) {
    let sum = 0;
    for (const ch of String(name ?? "")) sum += ch.charCodeAt(0);
    return AVATAR_TONES[sum % AVATAR_TONES.length];
}

export function initials(name) {
    return (
        String(name ?? "")
            .split(" ")
            .filter(Boolean)
            .slice(0, 2)
            .map((w) => w[0].toUpperCase())
            .join("") || "?"
    );
}
